import './App.css';
import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import ContactUsForm from './components/contactUsForm';
import NavBar from './components/NavBar';
import Catalogue from './pages/public/Catalogue';
import AboutUs from './components/AboutUs';
import TermsPopup from './components/TermsPopup';
import Admin from './pages/admin/Admin';
import AddSubfamilia from './pages/admin/AddSubfamilia';
import AddFamilia from './pages/admin/AddFamilia';
import AddProducto from './pages/admin/AddProducto';
import EditSubfamilia from './pages/admin/EditSubfamilia';
import EditFamilia from './pages/admin/EditFamilia';
import EditProducto from './pages/admin/EditProducto';
import UploadBanners from './pages/admin/UploadBanners';
import UploadFamiliaThumbnails from './pages/admin/UploadFamiliaThumbnails';
import AdminNavBar from './components/AdminNavBar';
import supabase from './utils/supabaseClient';
import { storagePublicUrl } from './utils/storage';
import ProductDetail from './pages/public/ProductDetail';
import { CartProvider } from './contexts/CartContext';
import { SettingsProvider } from './contexts/SettingsContext';
import Cart from './pages/public/Cart';
import Checkout from './pages/public/Checkout';

function WhatsAppButton({ lang }) {
  const phoneNumber = "50687839222"; // Replace with actual WhatsApp number
  const message = lang === 'es' 
    ? "¡Hola! Me interesa conocer más sobre sus productos."
    : "Hello! I'm interested in learning more about your products.";
  
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
  
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-button"
      aria-label={lang === 'es' ? 'Contactar por WhatsApp' : 'Contact via WhatsApp'}
    >
      <img 
        src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" 
        alt="WhatsApp" 
        className="whatsapp-logo-img"
      />
    </a>
  );
}

function Banner() {
  const [desktopBanners, setDesktopBanners] = useState([]);
  const [mobileBanners, setMobileBanners] = useState([]);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [fade, setFade] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch banners from database
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        // Fetch desktop banners
        const { data: desktopData, error: desktopError } = await supabase
          .from('banners')
          .select('url')
          .eq('desktop', true)
          .order('id', { ascending: true });

        if (desktopError) {
          console.error('Error fetching desktop banners:', desktopError);
        } else if (desktopData) {
          setDesktopBanners(desktopData.map(banner => banner.url).filter(url => url));
        }

        // Fetch mobile banners
        const { data: mobileData, error: mobileError } = await supabase
          .from('banners')
          .select('url')
          .eq('desktop', false)
          .order('id', { ascending: true });

        if (mobileError) {
          console.error('Error fetching mobile banners:', mobileError);
        } else if (mobileData) {
          setMobileBanners(mobileData.map(banner => banner.url).filter(url => url));
        }
      } catch (error) {
        console.error('Error fetching banners:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  // Preload banner images in the background
  useEffect(() => {
    const banners = isMobile ? mobileBanners : desktopBanners;
    
    if (banners.length === 0) return;
    
    // Preload all banners except the first one (which is already displayed)
    banners.forEach((bannerUrl, index) => {
      if (index === 0) return; // Skip first banner as it's already being displayed
      
      const image = new Image();
      image.src = bannerUrl;
    });
  }, [desktopBanners, mobileBanners, isMobile]);

  useEffect(() => {
    const banners = isMobile ? mobileBanners : desktopBanners;
    
    // Only start rotation if we have banners loaded
    if (banners.length === 0) return;

    const interval = setInterval(() => {
      setFade(true);
      setTimeout(() => {
        setCurrentBanner((prevBanner) => (prevBanner + 1) % banners.length);
        setFade(false);
      }, 1000);
    }, 10000); 

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
    };
  }, [desktopBanners, mobileBanners, isMobile, currentBanner]);

  // Reset current banner when switching between mobile/desktop
  useEffect(() => {
    setCurrentBanner(0);
  }, [isMobile]);

  const banners = isMobile ? mobileBanners : desktopBanners;

  // Show loading state or fallback if no banners
  if (loading) {
    return (
      <div className="hero-banner">
        <div className="banner-container" style={{ height: '400px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div>Cargando banners...</div>
        </div>
      </div>
    );
  }

  // Fallback to default banners if database is empty
  if (banners.length === 0) {
    const fallbackBanners = isMobile ? [
      storagePublicUrl('resources', 'banner mobile/Camitex Banner Web_640x800-03.jpg'),
      storagePublicUrl('resources', 'banner mobile/Camitex Banner Web_640x800-04.jpg')
    ] : [
      storagePublicUrl('resources', 'banner desktop/Camitex Banner Web_1920x600-01.jpg'),
      storagePublicUrl('resources', 'banner desktop/Camitex Banner Web_1920x600-02.jpg')
    ];

    return (
      <div className="hero-banner">
        <div className="banner-container">
          <img
            src={fallbackBanners[currentBanner % fallbackBanners.length]}
            alt="Banner"
            className={`banner-image ${fade ? "fade-out" : "fade-in"}`}
            loading="eager"
            decoding="async"
          />
          
          {/* Navigation arrows */}
          <button
            className="banner-nav-button prev"
            onClick={() => setCurrentBanner((prev) => (prev - 1 + fallbackBanners.length) % fallbackBanners.length)}
            aria-label="Previous banner"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button
            className="banner-nav-button next"
            onClick={() => setCurrentBanner((prev) => (prev + 1) % fallbackBanners.length)}
            aria-label="Next banner"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-banner">
      <div className="banner-container">
        <img
          src={banners[currentBanner]}
          alt="Banner"
          className={`banner-image ${fade ? "fade-out" : "fade-in"}`}
          loading="eager"
          decoding="async"
        />
        
        {/* Navigation arrows */}
        <button
          className="banner-nav-button prev"
          onClick={() => setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length)}
          aria-label="Previous banner"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <button
          className="banner-nav-button next"
          onClick={() => setCurrentBanner((prev) => (prev + 1) % banners.length)}
          aria-label="Next banner"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}

const translations = {
  es: {
    title: "CAMITEX",
    subtitle: "Somos una empresa textil orgullosamente tica, que ofrece a sus clientes y consumidores soluciones para sus emprendimientos e ideas!",
    shopNow: "Comprar ahora",
    whyUs: "¿Por qué trabajar con nosotros?",
    features: [
      "✅ Amplia variedad de productos",
      "✅ Envíos a todo el país",
      "✅ Atención personalizada"
    ],
    featured: "Los favoritos de nuestros clientes",
    codeLabel: "CÓDIGO:",
    retailLabel: "DETALLE",
    wholesaleLabel: "POR MAYOR (+12)",
    
    addToCart: "Agregar al carrito",
    copyright: "© 2025 Distribuidora CAMITEX. Todos los derechos reservados.",
    contact: {
      title: "Cotize con nosotros",
      name: "Nombre",
      email: "Correo electrónico",
      phone: "Número de teléfono",
      message: "Mensaje",
      send: "Enviar"
    }
  },
  en: {
    title: "CAMITEX",
    subtitle: "We are a proudly Costa Rican textile company, offering our clients and consumers textile solutions.",
    shopNow: "Shop Now",
    whyUs: "Why work with us?",
    features: [
      "✅ Wide variety of products",
      "✅ Nationwide shipping",
      "✅ Personalized attention"
    ],
    featured: "Our Customers' Favorites",
    codeLabel: "CODE:",
    retailLabel: "retail",
    wholesaleLabel: "WHOLESALE (+12)",
    products: [
      { 
        name: "Cotton T-Shirts", 
        code: "TSHIRT01", 
        retailPrice: "₡2500", 
        wholesalePrice: "₡2200", 
        wholesaleNote: "wholesale (+12)" 
      },
      { 
        name: "Plain Towel 30X30CM", 
        code: "TA30X30B", 
        retailPrice: "₡490", 
        wholesalePrice: "₡450", 
        wholesaleNote: "wholesale (+12)" 
      },
      { 
        name: "Polo T-Shirts Men (S-XL)", 
        code: "POLO01", 
        retailPrice: "₡4290", 
        wholesalePrice: "₡3700", 
        wholesaleNote: "wholesale (+12)" 
      },
      { 
        name: "CAMITEX POLO TYPE MEN: 2XL",
        code: "POLO2XL",
        material: "WAFFIT",
        retailPrice: "₡5500",
        wholesalePrice: "₡4500",
        wholesaleNote: "wholesale (+12)"
      },
      { 
        name: "ANIMAL PRINT TOWEL 70X130CM",
        code: "TM70x130E",
        retailPrice: "₡3950",
        wholesalePrice: "₡3500",
        wholesaleNote: "wholesale (+12)"
      }
    ],
    addToCart: "Add to Cart",
    copyright: "© 2025 CAMITEX Store. All rights reserved.",
    contact: {
      title: "Get a Quote for our products",
      name: "Name",
      email: "Email",
      phone: "Phone Number",
      message: "Message",
      send: "Send"
    }
  }
};


function Location({ lang }) {
  return (
    <section className="location-section">
      <div className="section-container">
        <div className="section-header">
          <h2 className="section-title">{lang === 'es' ? 'Nuestra Ubicación' : 'Our Location'}</h2>
          <div className="section-divider"></div>
        </div>
        <p className="location-description">
          {lang === 'es'
            ? 'Encuéntranos fácilmente en Costa Rica, Cartago. Cruce de Taras contiguo a la Policía de Tránsito.'
            : 'Find us easily in Costa Rica, Cartago. Taras crossing, next to the traffic police station.'}
        </p>
        
        <div className="map-container">
          <iframe
            title="Google Maps Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3930.5988722857032!2d-83.9358762!3d9.8839807!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8fa0e0880758a823%3A0xc84749cce4d3d0b0!2sCentro%20De%20Camisetas%20CAMITEX!5e0!3m2!1sen!2scr!4v1748311074990!5m2!1sen!2scr"
            width="100%"
            height="300"
            style={{ border: 0, borderRadius: 12 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
        
        <div className="location-links">
          <a
            href="https://maps.app.goo.gl/AZvUEFYwX13bZZGq7"
            target="_blank"
            rel="noopener noreferrer"
            className="location-link google-maps"
          >
            <span className="link-icon">📍</span>
            Google Maps
          </a>
          <a
            href="https://www.waze.com/en/live-map/directions/cr/provincia-de-cartago/cartago/camitex,-telas-y-camisetas?place=ChIJI6hYB4jgoI8RsNDT5MxJR8g"
            target="_blank"
            rel="noopener noreferrer"
            className="location-link waze"
          >
            <span className="link-icon">🚗</span>
            Waze
          </a>
        </div>
      </div>
    </section>
  );
}

function Contact({ lang }) {
  return (
    <div>
      <ContactUsForm lang={lang} />
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
        
      </div>
    </div>
  );
}

function FacebookSection({ lang }) {
  return (
    <section className="facebook-section">
      <div className="section-container">
        <div className="section-header">
          <h2 className="section-title">
            {lang === 'es' ? 'Promociones y Noticias' : 'Promotions and News'}
          </h2>
          <div className="section-divider"></div>
        </div>
        <div className="facebook-embed-container">
          <iframe
            title="Facebook Page"
            src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2FCamitexcr&tabs=timeline&width=340&height=500&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true&appId"
            width="340"
            height="500"
            style={{ border: 'none', overflow: 'hidden', borderRadius: 12 }}
            scrolling="no"
            frameBorder="0"
            loading="lazy"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            allowFullScreen={true}
            onError={(e) => {
              // Suppress Facebook SDK errors
              console.log('Facebook embed loaded with minor issues (non-critical)');
            }}
          ></iframe>
        </div>
      </div>
    </section>
  );
}

function SocialNetworksSection({ lang }) {
  const socialNetworks = [
    {
      name: 'Facebook',
      url: 'https://www.facebook.com/Camitexcr',
      icon: 'https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg',
      color: '#1877F2',
      gradient: 'linear-gradient(135deg, #1877F2 0%, #42A5F5 100%)'
    },
    {
      name: 'Instagram',
      url: 'https://www.instagram.com/camitexcr/?hl=en',
      icon: 'https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png',
      color: '#E4405F',
      gradient: 'linear-gradient(135deg, #833AB4 0%, #E4405F 50%, #F77737 100%)'
    },
    {
      name: 'TikTok',
      url: 'https://www.tiktok.com/@camitexcostarica',
      icon: 'https://cdn.jsdelivr.net/npm/simple-icons@v6/icons/tiktok.svg',
      color: '#000000',
      gradient: 'linear-gradient(135deg, #FF0050 0%, #000000 100%)'
    }
  ];

  return (
    <section className="social-networks-section">
      <div className="section-container">
        <div className="section-header">
          <h2 className="section-title">
            {lang === 'es' ? 'Síguenos en Redes Sociales' : 'Follow us on Social Media'}
          </h2>
          <div className="section-divider"></div>
        </div>
        <div className="social-networks-grid">
          {socialNetworks.map((network, index) => (
            <a
              key={index}
              href={network.url}
              target="_blank"
              rel="noopener noreferrer"
              className="social-network-card"
              style={{ '--gradient': network.gradient }}
            >
              <div className="social-icon-container">
                <img
                  src={network.icon}
                  alt={network.name}
                  className="social-icon"
                />
              </div>
              <span className="social-name">{network.name}</span>
              <div className="social-card-overlay"></div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function Home({ lang, t }) {
  return (
    <div className="home-page">
      <Banner />
      
      {/* CTA Section */}
      <section className="cta-section">
        <div className="section-container">
          <div className="cta-content">
            <h2 className="cta-title">{lang === 'es' ? 'Explora Nuestro Catálogo Completo' : 'Explore Our Complete Catalogue'}</h2>
            <p className="cta-description">
              {lang === 'es' ? 'Descubre nuestra amplia gama de productos textiles de alta calidad.' : 'Discover our wide range of high-quality textile products.'}
            </p>
            <Link to="/catalogue" className="cta-button">
              <span>{lang === 'es' ? 'Ver Catálogo' : 'View Catalogue'}</span>
              <svg className="cta-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Content Grid */}
      <div className="home-content-grid">
        <div className="contact-column">
          <Contact lang={lang} />
        </div>
        <div className="social-column">
          <FacebookSection lang={lang} />
          <SocialNetworksSection lang={lang} />
        </div>
        <div className="location-column">
          <Location lang={lang} />
        </div>
      </div>
    </div>
  );
}

// Component to scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const [lang, setLang] = useState('es');
  const t = translations[lang];
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Fetch the authenticated user
    
    supabase.auth.getUser().then(({ data: { user } }) => setUser(user));
  }, []);

  return (
    <CartProvider>
      <SettingsProvider>
      <Router>
        <ScrollToTop />
        <div className="App">
          <div className="yellow-banner" style={{ backgroundColor: '#FFD700', textAlign: 'center', padding: '10px 0', fontWeight: 'bold' }}>
            {lang === 'es' 
              ? '🚧  ¡Nuestra página está en desarrollo! Pero no dudes en contactarnos. 😊' 
              : '🚧   Our website is under development! Feel free to contact us. 😊'}
          </div>
          <NavBar lang={lang} setLang={setLang} />

         

          <Routes>
            <Route path="/" element={<Home lang={lang} t={t} />} />
            <Route path="/catalogue" element={<Catalogue lang={lang} />} />
            <Route path="/location" element={<Location lang={lang} />} />
            <Route path="/about" element={<AboutUs lang={lang} />} />
            <Route path="/contact" element={<Contact lang={lang} />} />
            <Route path="/admin" element={<Admin lang={lang} />} />
            <Route path="/add-subfamilia" element={<AddSubfamilia lang={lang} />} />
            <Route path="/add-familia" element={<AddFamilia />} />
            <Route path="/add-producto" element={<AddProducto />} />
            <Route path="/edit-subfamilia" element={<EditSubfamilia />} />
            <Route path="/edit-familia" element={<EditFamilia />} />
            <Route path="/edit-producto" element={<EditProducto />} />
            <Route path="/edit-producto/:id" element={<EditProducto />} />
            <Route path="/upload-banners" element={<UploadBanners />} />
            <Route path="/upload-familia-thumbnails" element={<UploadFamiliaThumbnails />} />
            <Route path="/product/:subfamiliaId" element={<ProductDetail lang={lang} />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout lang={lang} />} />
          </Routes>
          
          {/* Floating WhatsApp Button */}
          <WhatsAppButton lang={lang} />
          <footer className="App-footer">
            <p>
              {t.copyright}
              <TermsPopup lang={lang} />
            </p>
          </footer>
        </div>
      </Router>
      </SettingsProvider>
    </CartProvider>
  );
}

export default App;
